import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Divider,
  Stack,
  Grid,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import SaveIcon from "@mui/icons-material/Save";
import { PictureAsPdf, Delete, Add, ContentCopy, HelpOutline, Lock, CheckCircle } from "@mui/icons-material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Stop, ExpandMore, Visibility, VisibilityOff } from "@mui/icons-material";
import { saveCalendarSettings, authenticateGoogleCalendar, authenticateOutlookCalendar } from "../api/actions/mutation";
import { listCustomers } from "../../user/api/actions/query";
import { fetchRoles } from "../../roles/api/query";
import { useDispatch } from "react-redux";
import { messageShow } from "../../common/api/actions";

const defaultWorkingHours = [
  { day: "Monday", isClosed: false, startTime: '09:00', endTime: '17:00' },
  { day: "Tuesday", isClosed: false, startTime: '09:00', endTime: '17:00' },
  { day: "Wednesday", isClosed: false, startTime: '09:00', endTime: '17:00' },
  { day: "Thursday", isClosed: false, startTime: '09:00', endTime: '17:00' },
  { day: "Friday", isClosed: false, startTime: '09:00', endTime: '17:00' },
  { day: "Saturday", isClosed: false, startTime: '09:00', endTime: '17:00' },
  { day: "Sunday", isClosed: false, startTime: '09:00', endTime: '17:00' },
];

const Setup = React.forwardRef(({ 
  canEdit = true, 
  initialSettings = {}, 
  onSettingsUpdate = () => { }, 
  activeTab = 'google', 
  onTabChange = () => { },
  selectedConsultationTypeTab = 0,
  onSelectConsultationTypeTab = () => {}
}, ref) => {
  const dispatch = useDispatch();
  const [adminUsers, setAdminUsers] = useState([]);

  useEffect(() => {
    const fetchAdminUsersAndRoles = async () => {
      try {
        const [rolesResult, usersResult] = await Promise.allSettled([
          fetchRoles(),
          dispatch(listCustomers({ page: 1, limit: 100, type: "assignRole", role: "admin" }))
        ]);

        const allRoles = rolesResult.status === "fulfilled" ? (rolesResult.value || []) : [];
        const userList = usersResult.status === "fulfilled" ? (usersResult.value?.data?.data?.list || []) : [];

        if (userList.length > 0) {
          const mappedUsers = userList.map(u => {
            let roleName = "No Role";
            if (u.roleAssigned) {
              const matchedRole = allRoles.find(r => (r._id || "").toString() === (u.roleAssigned || "").toString());
              if (matchedRole?.name) {
                roleName = matchedRole.name;
              }
            } else if (u.roleDetails?.name) {
              roleName = u.roleDetails.name;
            } else if (u.roleName) {
              roleName = u.roleName;
            } else if (typeof u.role === 'string' && u.role !== 'admin') {
              roleName = u.role;
            }
            return {
              ...u,
              roleDisplayName: roleName
            };
          });
          setAdminUsers(mappedUsers);
        }
      } catch (e) {
        console.error("Error fetching admin users and roles:", e);
      }
    };
    fetchAdminUsersAndRoles();
  }, [dispatch]);

  React.useImperativeHandle(ref, () => ({
    openAddTypeModal: () => openTypeModal('add'),
    deleteType: (index) => handleDeleteType(index),
    saveSettings: () => handleSave(),
    getCurrentEmail: () => (currentData?.email || currentData?.calendarEmail || '').trim()
  }));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isSaving, setIsSaving] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [isCustomUrl, setIsCustomUrl] = useState(false);
  const currencySymbol = initialSettings?.currencySymbol || "₹";
  const prevSettingsRef = React.useRef(null);

  // Helper to normalize payment config for state
  const normalizePaymentState = (payConfig) => {
    const existingKeys = (payConfig?.keys || []).filter(k => k && k.type);
    const hasTest = existingKeys.some(k => k.type === "test");
    const hasProd = existingKeys.some(k => k.type === "production");
    let keys = [...existingKeys];
    if (!hasTest) keys.push({ type: "test", isActive: !hasProd, keyId: "", keySecret: "", webhookSecret: "" });
    if (!hasProd) keys.push({ type: "production", isActive: false, keyId: "", keySecret: "", webhookSecret: "" });
    return { isEnabled: payConfig?.isEnabled || false, keys: keys };
  };

  // Helper to format fee to at most 2 decimal places
  const formatFeeTo2Decimals = (val) => {
    if (val === '' || val === null || val === undefined) return val;
    const str = String(val);
    if (str.includes('.')) {
      const [intPart, decPart] = str.split('.');
      if (decPart && decPart.length > 2) {
        return `${intPart}.${decPart.slice(0, 2)}`;
      }
    }
    return val;
  };

  // Initialize provider-specific data
  const getInitialProviderData = (provider, settings) => {
    const config = provider === 'google' ? settings?.googleConfig : settings?.outlookConfig;
    let fees = (config?.locationFees || []).map(f => ({
      ...f,
      consultationFee: formatFeeTo2Decimals(f.consultationFee)
    }));

    // Ensure "Other Countries" (default) entry exists
    if (!fees.some(f => f.isDefault)) {
      fees = [{
        isDefault: true,
        countryName: "Other Countries",
        consultationFee: formatFeeTo2Decimals(config?.consultationFee || 0)
      }, ...fees];
    }

    const rawPayConfig = config?.paymentConfiguration || settings?.paymentConfiguration || settings?.paymentConfig;

    return {
      name: config?.name || settings?.name || "General Consultation (Default)",
      description: config?.description || settings?.description || "",
      email: config?.email || settings?.googleConfig?.email || settings?.outlookConfig?.email || settings?.email || "",
      eventDurationMinutes: config?.eventDurationMinutes || settings?.eventDurationMinutes || 15,
      meetingDurationMinutes: config?.meetingDurationMinutes || settings?.meetingDurationMinutes || 15,
      formType: config?.formType || settings?.formType || "full",
      paymentMode: config?.paymentMode || settings?.paymentMode || "unpaid",
      consultationFee: formatFeeTo2Decimals(config?.consultationFee || settings?.consultationFee || 0),
      particulars: config?.particulars || settings?.particulars || "Consultation Services",
      sacCode: config?.sacCode || settings?.sacCode || "998314",
      locationFees: fees,
      leadStatus: config?.leadStatus || settings?.leadStatus || "Contact",
      notifyByEmail: !!(config?.notifyByEmail ?? settings?.notifyByEmail),
      notifyBySMS: !!(config?.notifyBySMS ?? settings?.notifyBySMS),
      workingHours: (config?.workingHours?.length === 7 ? config.workingHours : (settings?.workingHours?.length === 7 ? settings.workingHours : defaultWorkingHours)),
      isSameDayBookingAllowed: config?.isSameDayBookingAllowed ?? settings?.isSameDayBookingAllowed ?? true,
      isActive: config?.isActive ?? settings?.isActive ?? true,
      paymentConfiguration: normalizePaymentState(rawPayConfig),
      consultationTypes: ((config?.consultationTypes && config.consultationTypes.length > 0) ? config.consultationTypes : (settings?.consultationTypes || [])).map(t => ({
        ...t,
        consultationFee: formatFeeTo2Decimals(t.consultationFee || 0),
        locationFees: (t.locationFees || []).map(f => ({
          ...f,
          consultationFee: formatFeeTo2Decimals(f.consultationFee)
        })),
        googleOauthTokens: t.googleOauthTokens || null,
        outlookOauthTokens: t.outlookOauthTokens || null
      })),
      ...(provider === 'google' && { calendarUrl: config?.calendarUrl || settings?.calendarUrl || "" })
    };
  };

  const [googleData, setGoogleData] = useState(() => getInitialProviderData('google', initialSettings));
  const [outlookData, setOutlookData] = useState(() => getInitialProviderData('outlook', initialSettings));

  const [availableCountries, setAvailableCountries] = useState(() => initialSettings?.availableCountries || []);
  const [loadingCountries, setLoadingCountries] = useState(() => !(initialSettings?.availableCountries && initialSettings.availableCountries.length > 0));

  const resolveActiveProvider = (settings) => {
    const isGoogleEnabled = settings?.isCalendarGoogleEnabled !== false;
    const isOutlookEnabled = settings?.isCalendarOutlookEnabled !== false;
    let provider = settings?.calendarProvider || "google";

    if (!isGoogleEnabled && provider === "google" && isOutlookEnabled) {
      return "outlook";
    }
    if (!isOutlookEnabled && provider === "outlook" && isGoogleEnabled) {
      return "google";
    }
    return provider;
  };

  const [calendarProvider, setCalendarProvider] = useState(() => resolveActiveProvider(initialSettings));

  const isGoogleBtnEnabled = initialSettings?.isCalendarGoogleEnabled !== false && initialSettings?.isCalendarGoogleEnabled !== "false";
  const isOutlookBtnEnabled = initialSettings?.isCalendarOutlookEnabled !== false && initialSettings?.isCalendarOutlookEnabled !== "false";
  // Derive current active base data based on activeTab
  const baseData = activeTab === 'google' ? googleData : outlookData;
  const currentConsultationType = (selectedConsultationTypeTab > 0 && baseData.consultationTypes && baseData.consultationTypes[selectedConsultationTypeTab - 1])
    ? baseData.consultationTypes[selectedConsultationTypeTab - 1]
    : null;

  const currentData = currentConsultationType ? {
    ...baseData,
    ...currentConsultationType,
    name: currentConsultationType.name !== undefined ? currentConsultationType.name : (baseData.name || "General Consultation (Default)"),
    description: currentConsultationType.description !== undefined ? currentConsultationType.description : (baseData.description || ""),
    email: currentConsultationType.calendarEmail || currentConsultationType.email || currentConsultationType.assignedManagerEmail || "",
    calendarEmail: currentConsultationType.calendarEmail || currentConsultationType.email || currentConsultationType.assignedManagerEmail || "",
    assignedManagerEmail: currentConsultationType.assignedManagerEmail || "",
    assignedManagerName: currentConsultationType.assignedManagerName || "",
    assignedManagerId: currentConsultationType.assignedManagerId || "",
    calendarUrl: currentConsultationType.calendarUrl || (currentConsultationType.calendarEmail || currentConsultationType.assignedManagerEmail ? `https://calendar.google.com/calendar/embed?src=${(currentConsultationType.calendarEmail || currentConsultationType.assignedManagerEmail).replace("@", "%40")}` : ""),
    meetingDurationMinutes: currentConsultationType.meetingDurationMinutes || baseData.meetingDurationMinutes || 15,
    eventDurationMinutes: currentConsultationType.eventDurationMinutes || baseData.eventDurationMinutes || 15,
    formType: currentConsultationType.formType || baseData.formType || "full",
    paymentMode: currentConsultationType.paymentMode || baseData.paymentMode || "unpaid",
    consultationFee: formatFeeTo2Decimals(currentConsultationType.consultationFee || baseData.consultationFee || 0),
    particulars: currentConsultationType.particulars || baseData.particulars || "Consultation Services",
    sacCode: currentConsultationType.sacCode || baseData.sacCode || "998314",
    leadStatus: currentConsultationType.leadStatus || baseData.leadStatus || "Contact",
    notifyByEmail: currentConsultationType.notifyByEmail ?? baseData.notifyByEmail ?? false,
    notifyBySMS: currentConsultationType.notifyBySMS ?? baseData.notifyBySMS ?? false,
    isSameDayBookingAllowed: currentConsultationType.isSameDayBookingAllowed ?? baseData.isSameDayBookingAllowed ?? true,
    locationFees: (currentConsultationType.locationFees?.length > 0 ? currentConsultationType.locationFees : baseData.locationFees).map(f => ({
      ...f,
      consultationFee: formatFeeTo2Decimals(f.consultationFee)
    })),
    workingHours: currentConsultationType.workingHours?.length === 7 ? currentConsultationType.workingHours : baseData.workingHours,
    paymentConfiguration: currentConsultationType.paymentConfiguration || baseData.paymentConfiguration
  } : baseData;

  const updateCurrentData = (updates) => {
    if (selectedConsultationTypeTab > 0) {
      const targetIdx = selectedConsultationTypeTab - 1;
      const updater = prev => {
        const types = [...(prev.consultationTypes || [])];
        if (types[targetIdx]) {
          types[targetIdx] = { ...types[targetIdx], ...updates };
        }
        return { ...prev, consultationTypes: types };
      };
      if (activeTab === 'google') setGoogleData(updater);
      else setOutlookData(updater);
    } else {
      setGoogleData(prev => ({ ...prev, ...updates }));
      setOutlookData(prev => ({ ...prev, ...updates }));
    }
  };

  // Keep local provider data in sync when initialSettings changes from parent
  useEffect(() => {
    if (initialSettings && initialSettings._id) {
      setGoogleData(getInitialProviderData('google', initialSettings));
      setOutlookData(getInitialProviderData('outlook', initialSettings));
      if (initialSettings.availableCountries?.length) {
        setAvailableCountries(initialSettings.availableCountries);
      }
    }
  }, [initialSettings]);

  // Sync calendarProvider with activeTab prop to ensure dropdown matches the view
  useEffect(() => {
    if (activeTab && activeTab !== calendarProvider) {
      setCalendarProvider(activeTab);
    }
  }, [activeTab, calendarProvider]);

  const [showSecrets, setShowSecrets] = useState({ testKeySecret: false, productionKeySecret: false });
  const [selectedTypeTab, setSelectedTypeTab] = useState(0);
  const [consultationTypeModal, setConsultationTypeModal] = useState({ open: false, mode: 'add', data: null, index: -1 });

  const openTypeModal = (mode = 'add', data = null, index = -1) => {
    const initialTypeData = data || {
      name: "",
      description: "",
      assignedManagerEmail: "",
      calendarEmail: "",
      calendarUrl: "",
      calendarProvider: activeTab || "google",
      eventDurationMinutes: 30,
      meetingDurationMinutes: 30,
      paymentMode: "unpaid",
      consultationFee: 0,
      leadStatus: "Contact",
      formType: "full",
      particulars: "Consultation Services",
      sacCode: "998314",
      isSameDayBookingAllowed: true,
      notifyByEmail: true,
      notifyBySMS: false,
      locationFees: [{ isDefault: true, countryName: "Other Countries", consultationFee: 0 }],
      workingHours: JSON.parse(JSON.stringify(defaultWorkingHours)),
      isActive: true
    };
    setConsultationTypeModal({ open: true, mode, data: initialTypeData, index });
  };

  const closeTypeModal = () => setConsultationTypeModal({ open: false, mode: 'add', data: null, index: -1 });

  const handleTypeDataChange = (field, value) => {
    setConsultationTypeModal(prev => ({
      ...prev,
      data: { ...prev.data, [field]: value }
    }));
  };

  const saveUpdatedTypesPayload = async (updatedTypes, successMsg) => {
    const newGoogleData = { ...googleData, consultationTypes: updatedTypes };
    const newOutlookData = { ...outlookData, consultationTypes: updatedTypes };

    setGoogleData(newGoogleData);
    setOutlookData(newOutlookData);

    try {
      setIsSaving(true);
      const payload = {
        calendarProvider,
        googleConfig: newGoogleData,
        outlookConfig: newOutlookData,
      };
      const response = await saveCalendarSettings(payload);
      if (response.success === false) throw new Error(response.message || "Failed to save consultation type");
      onSettingsUpdate(response.data || response);
      dispatch(messageShow(successMsg));
    } catch (err) {
      dispatch(messageShow(err.message || "Failed to save consultation type"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveType = async () => {
    const { mode, index, data } = consultationTypeModal;
    if (data?.paymentMode === "paid") {
      for (const fee of (data.locationFees || [])) {
        const feeVal = Number(fee.consultationFee);
        if (fee.consultationFee === '' || fee.consultationFee === null || fee.consultationFee === undefined || Number.isNaN(feeVal) || feeVal <= 0) {
          dispatch(messageShow(`Consultation fee for "${fee.countryName || (fee.isDefault ? 'Other Countries' : 'Location')}" must be greater than 0.`));
          return;
        }
        const feeStr = String(fee.consultationFee);
        if (feeStr.includes('.')) {
          const decPart = feeStr.split('.')[1];
          if (decPart && decPart.length > 2) {
            dispatch(messageShow(`Consultation fee for "${fee.countryName || (fee.isDefault ? 'Other Countries' : 'Location')}" can have at most 2 decimal places.`));
            return;
          }
        }
      }
    }
    let currentTypes = googleData?.consultationTypes || outlookData?.consultationTypes || [];
    let updatedTypes = [...currentTypes];
    
    if (mode === 'add') {
      updatedTypes.push(data);
    } else if (index >= 0) {
      updatedTypes[index] = data;
    }
    
    closeTypeModal();
    await saveUpdatedTypesPayload(updatedTypes, "Consultation type saved successfully!");
  };

  const handleDeleteType = async (index) => {
    let currentTypes = googleData?.consultationTypes || outlookData?.consultationTypes || [];
    const updatedTypes = currentTypes.filter((_, i) => i !== index);
    await saveUpdatedTypesPayload(updatedTypes, "Consultation type deleted successfully!");
  };

  const handleTypeLocationFeeChange = (idx, field, value) => {
    const fees = [...consultationTypeModal.data.locationFees];
    if (field === 'countryCodeId') {
      const country = availableCountries.find(c => c._id === value);
      fees[idx] = { ...fees[idx], countryCodeId: value, countryName: country?.name || '' };
    } else if (field === 'consultationFee') {
      if (value === '') {
        fees[idx] = { ...fees[idx], consultationFee: '' };
      } else {
        let cleanVal = value;
        if (cleanVal.includes('.')) {
          const [intPart, decPart] = cleanVal.split('.');
          if (decPart && decPart.length > 2) {
            cleanVal = `${intPart}.${decPart.slice(0, 2)}`;
          }
        }
        const num = Number(cleanVal);
        if (num < 0) return; // Prevent negative values
        fees[idx] = { ...fees[idx], consultationFee: cleanVal };
      }
      if (fees[idx].isDefault) {
        handleTypeDataChange('consultationFee', fees[idx].consultationFee === '' ? '' : Number(fees[idx].consultationFee));
      }
    } else {
      fees[idx] = { ...fees[idx], [field]: value };
    }
    handleTypeDataChange('locationFees', fees);
  };

  const handleAddTypeLocationFee = () => {
    const usedIds = (consultationTypeModal.data.locationFees || []).filter(f => !f.isDefault).map(f => f.countryCodeId);
    const country = availableCountries.find(c => !usedIds.includes(c._id));
    if (!country) return;
    handleTypeDataChange('locationFees', [...consultationTypeModal.data.locationFees, { countryCodeId: country._id, countryName: country.name, consultationFee: 0, isDefault: false }]);
  };

  const handleTypeTimeChange = (dayIdx, field, val) => {
    const hours = [...consultationTypeModal.data.workingHours];
    hours[dayIdx][field] = val ? val.format('HH:mm') : '09:00';
    handleTypeDataChange('workingHours', hours);
  };

  const toggleTypeDayClosed = (dayIdx) => {
    const hours = [...consultationTypeModal.data.workingHours];
    hours[dayIdx].isClosed = !hours[dayIdx].isClosed;
    handleTypeDataChange('workingHours', hours);
  };

  useEffect(() => {
    if (initialSettings) {
      if (initialSettings.availableCountries && initialSettings.availableCountries.length > 0) {
        setAvailableCountries(initialSettings.availableCountries);
        setLoadingCountries(false);
      }

      // Only re-initialize internal provider data if initialSettings reference changed (e.g. after save)
      if (initialSettings !== prevSettingsRef.current) {
        setCalendarProvider(resolveActiveProvider(initialSettings));
        setGoogleData(getInitialProviderData('google', initialSettings));
        setOutlookData(getInitialProviderData('outlook', initialSettings));
        prevSettingsRef.current = initialSettings;
      }
    }
  }, [initialSettings]);

  const handleAddLocationFee = () => {
    if (loadingCountries) return;
    if (availableCountries.length === 0) return;
    const usedCountryIds = (currentData.locationFees || []).filter(f => !f.isDefault).map(f => f.countryCodeId);
    const availableCountry = availableCountries.find(c => !usedCountryIds.includes(c._id));
    if (!availableCountry) { alert("All countries already configured."); return; }
    updateCurrentData({
      locationFees: [...(currentData.locationFees || []), {
        countryCodeId: availableCountry._id,
        countryName: availableCountry.name,
        consultationFee: 0,
        isDefault: false,
      }]
    });
  };

  const handleRemoveLocationFee = (countryId) => {
    updateCurrentData({ locationFees: currentData.locationFees.filter(f => f.isDefault || f.countryCodeId !== countryId) });
  };

  const handleLocationFeeChange = (index, field, value) => {
    const updated = currentData.locationFees.map((f, i) => {
      if (i === index) {
        if (field === 'countryCodeId') {
          const country = availableCountries.find(c => c._id === value);
          return { ...f, countryCodeId: value, countryName: country?.name || '' };
        } else if (field === 'consultationFee') {
          if (value === '') {
            return { ...f, consultationFee: '' };
          }
          let cleanVal = value;
          if (cleanVal.includes('.')) {
            const [intPart, decPart] = cleanVal.split('.');
            if (decPart && decPart.length > 2) {
              cleanVal = `${intPart}.${decPart.slice(0, 2)}`;
            }
          }
          const num = Number(cleanVal);
          if (num < 0) return f; // Prevent negative values
          return { ...f, consultationFee: cleanVal };
        }
      }
      return f;
    });
    updateCurrentData({ locationFees: updated });
  };

  const getAvailableCountriesForDropdown = (currentCountryId) => {
    const usedCountryIds = (currentData.locationFees || []).filter(f => !f.isDefault && f.countryCodeId !== currentCountryId).map(f => f.countryCodeId);
    return availableCountries.filter(c => !usedCountryIds.includes(c._id));
  };

  const handleTimeChange = (dayIndex, field, newValue) => {
    const updatedHours = [...currentData.workingHours];
    updatedHours[dayIndex][field] = newValue ? newValue.format('HH:mm') : '12:00';
    updateCurrentData({ workingHours: updatedHours });
  };

  const toggleDayClosed = (dayIndex) => {
    const updatedHours = [...currentData.workingHours];
    updatedHours[dayIndex].isClosed = !updatedHours[dayIndex].isClosed;
    updateCurrentData({ workingHours: updatedHours });
  };

  const handlePaymentConfigChange = (field, value) => {
    if (field === 'isEnabled') {
      updateCurrentData({
        paymentConfiguration: {
          ...currentData.paymentConfiguration,
          isEnabled: value
        }
      });
    }
  };

  const handleKeyUpdate = (keyType, field, value) => {
    const updatedKeys = currentData.paymentConfiguration.keys.map(key => {
      if (key.type === keyType) {
        return { ...key, [field]: value };
      }
      return key;
    });
    updateCurrentData({
      paymentConfiguration: {
        ...currentData.paymentConfiguration,
        keys: updatedKeys
      }
    });
  };

  const handleSetActiveKey = (keyType) => {
    const updatedKeys = currentData.paymentConfiguration.keys.map(key => ({
      ...key,
      isActive: key.type === keyType
    }));
    updateCurrentData({
      paymentConfiguration: {
        ...currentData.paymentConfiguration,
        keys: updatedKeys
      }
    });
  };

  const handleSave = async () => {
    try {
      if (currentData.paymentMode === "paid") {
        for (const fee of (currentData.locationFees || [])) {
          const feeVal = Number(fee.consultationFee);
          if (fee.consultationFee === '' || fee.consultationFee === null || fee.consultationFee === undefined || Number.isNaN(feeVal) || feeVal <= 0) {
            dispatch(messageShow(`Consultation fee for "${fee.countryName || (fee.isDefault ? 'Other Countries' : 'Location')}" must be greater than 0.`));
            return;
          }
          const feeStr = String(fee.consultationFee);
          if (feeStr.includes('.')) {
            const decPart = feeStr.split('.')[1];
            if (decPart && decPart.length > 2) {
              dispatch(messageShow(`Consultation fee for "${fee.countryName || (fee.isDefault ? 'Other Countries' : 'Location')}" can have at most 2 decimal places.`));
              return;
            }
          }
        }
      }
      setIsSaving(true);
      const payload = {
        calendarProvider,
        googleConfig: googleData,
        outlookConfig: outlookData,
      };
      const response = await saveCalendarSettings(payload);
      if (response.success === false) throw new Error(response.message || "Failed to save");
      onSettingsUpdate(response.data || response);
      dispatch(messageShow("Settings saved successfully!"));
    } catch (err) {
      dispatch(messageShow(err.message || "Failed to save settings"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setGoogleData(getInitialProviderData('google', initialSettings));
    setOutlookData(getInitialProviderData('outlook', initialSettings));
    setCalendarProvider(initialSettings.calendarProvider || "google");
    // Reset payment config if needed
  };

  const handleStopMeeting = () => {
    updateCurrentData({ workingHours: currentData.workingHours.map(day => ({ ...day, isClosed: true })) });
  };

  const getActiveKey = () => {
    const config = currentData.paymentConfiguration;
    if (!config || !config.keys) return null;
    return config.keys.find(k => k.isActive) || config.keys[0];
  };

  const getWebhookUrl = () => {
    const isProd = getActiveKey()?.type === 'production';
    return isProd 
      ? "https://datumart-api.datumailabs.com/api/payment/razorpay/webhook" 
      : "https://datumart-test-api.datumailabs.com/api/payment/razorpay/webhook";
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 4, width: '100%', maxWidth: 1200, margin: '0 auto' }}>
      <Dialog open={manualOpen} onClose={() => setManualOpen(false)} fullWidth maxWidth="md">
        <DialogContent dividers sx={{ height: '75vh', p: 0 }}>
          <iframe
            key={`${activeTab}-${manualOpen}`}
            src={activeTab === 'outlook' ? "/OutlookCalendarIntegrationGuide.pdf#page=1" : "/GoogleCalendarIntegrationGuide.pdf#page=1"}
            title={activeTab === 'outlook' ? "Outlook User Manual" : "Google User Manual"}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        </DialogContent>
      </Dialog>

      <Box sx={{ maxWidth: isMobile ? '100%' : 1500 }}>
        <Grid container spacing={isMobile ? 1 : 3}>
          <Grid item xs={12} md={6}>
            {isGoogleBtnEnabled && isOutlookBtnEnabled ? (
              <FormControl fullWidth variant="standard">
                <InputLabel>Active Calendar Provider</InputLabel>
                <Select
                  value={calendarProvider}
                  onChange={(e) => {
                    setCalendarProvider(e.target.value);
                    onTabChange(e.target.value);
                  }}
                  disabled={!canEdit}
                >
                  <MenuItem value="google">Google Calendar</MenuItem>
                  <MenuItem value="outlook">Outlook Calendar</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Active Calendar Provider"
                variant="standard"
                fullWidth
                value={calendarProvider === 'google' ? 'Google Calendar' : 'Outlook Calendar'}
                InputProps={{ readOnly: true }}
                disabled={!canEdit}
              />
            )}
          </Grid>

          {/*<Grid item xs={12} md={6}>
            <TextField
              label="Timezone"
              variant="standard"
              fullWidth
              value={initialSettings?.timeZone || 'UTC'}
              InputProps={{ readOnly: true }}
              helperText="Timezone is managed in Common Settings"
            />
          </Grid>*/}


          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="standard">
              <InputLabel>Duration</InputLabel>
              <Select
                value={currentData.meetingDurationMinutes}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  updateCurrentData({
                    meetingDurationMinutes: val,
                    eventDurationMinutes: val,
                  });
                }}
                disabled={!canEdit}
              >
                <MenuItem value={15}>15 minutes</MenuItem>
                <MenuItem value={30}>30 minutes</MenuItem>
                <MenuItem value={60}>60 minutes (1 Hr)</MenuItem>
                <MenuItem value={90}>90 minutes (1.5 Hrs)</MenuItem>
                <MenuItem value={120}>120 minutes (2hrs)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {selectedConsultationTypeTab > 0 && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="standard">
                <InputLabel>Assigned Email (Roles & Permissions)</InputLabel>
                <Select
                  value={currentData.assignedManagerEmail || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    const selectedUser = adminUsers.find(u => u.email === val);
                    const defaultEmail = (baseData.email || '').toLowerCase();
                    const currentCalEmail = (currentData.calendarEmail || '').toLowerCase();
                    const updates = { assignedManagerEmail: val };
                    if (selectedUser) {
                      updates.assignedManagerName = selectedUser.name || selectedUser.firstName || '';
                      updates.assignedManagerId = selectedUser._id || '';
                      updates.assignedManagerRole = selectedUser.roleDisplayName || '';
                    }
                    // Auto-sync email & calendarUrl if empty or if set to default admin email
                    if (!currentCalEmail || currentCalEmail === defaultEmail) {
                      updates.calendarEmail = val;
                      updates.email = val;
                      if (activeTab === 'google' && val) {
                        updates.calendarUrl = `https://calendar.google.com/calendar/embed?src=${val.replace("@", "%40")}`;
                      }
                    }
                    updateCurrentData(updates);
                  }}
                  disabled={!canEdit}
                >
                  <MenuItem value="">
                    <em>Unassigned / Default Admin</em>
                  </MenuItem>
                  {currentData.assignedManagerEmail && !adminUsers.some(u => u.email === currentData.assignedManagerEmail) && (
                    <MenuItem value={currentData.assignedManagerEmail}>
                      {currentData.assignedManagerEmail}
                    </MenuItem>
                  )}
                  {adminUsers.map((user) => {
                    const roleName = user.roleDisplayName || 'Store Admin';
                    const displayName = `${roleName} — ${user.email}`;
                    return (
                      <MenuItem key={user._id || user.email} value={user.email}>
                        {displayName}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <TextField label="Email" variant="standard" fullWidth value={currentData.email}
              onChange={(e) => {
                const newEmail = e.target.value;
                const updates = { email: newEmail, calendarEmail: newEmail };
                if (!isCustomUrl && activeTab === 'google') {
                  updates.calendarUrl = `https://calendar.google.com/calendar/embed?src=${newEmail.replace("@", "%40")}`;
                }
                updateCurrentData(updates);
              }}
              disabled={!canEdit}
            />
          </Grid>

          {activeTab === 'google' && (
            <Grid item xs={12} md={6}>
              <TextField
                label="Calendar URL"
                variant="standard"
                fullWidth
                value={currentData.calendarUrl}
                onChange={(e) => { updateCurrentData({ calendarUrl: e.target.value }); setIsCustomUrl(true); }}
                placeholder="Google Embed URL"
                disabled={!canEdit}
              />
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="standard">
              <InputLabel>Default Lead Status</InputLabel>
              <Select
                value={currentData.leadStatus}
                onChange={(e) => updateCurrentData({ leadStatus: e.target.value })}
                disabled={!canEdit}
              >
                <MenuItem value="Contact">Contact</MenuItem>
                <MenuItem value="Lead">Lead</MenuItem>
                <MenuItem value="Prospect">Prospect</MenuItem>
                <MenuItem value="Customer">Customer</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="standard">
              <InputLabel>Form Type</InputLabel>
              <Select
                value={currentData.formType}
                onChange={(e) => updateCurrentData({ formType: e.target.value })}
                disabled={!canEdit}
              >
                <MenuItem value="mini">Mini Form</MenuItem>
                <MenuItem value="full">Full Form</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="standard">
              <InputLabel>Payment Mode</InputLabel>
              <Select
                value={currentData.paymentMode}
                onChange={(e) => updateCurrentData({ paymentMode: e.target.value })}
                disabled={!canEdit}
              >
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="unpaid">Unpaid</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Consultation Type Name"
              variant="standard"
              fullWidth
              value={currentData.name !== undefined ? currentData.name : "General Consultation (Default)"}
              onChange={(e) => updateCurrentData({ name: e.target.value })}
              disabled={!canEdit}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              helperText="Brief details or agenda about this consultation service"
              variant="standard"
              fullWidth
              multiline
              rows={2}
              value={currentData.description || ""}
              onChange={(e) => updateCurrentData({ description: e.target.value })}
              disabled={!canEdit}
              placeholder="Enter consultation description"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField 
              label="Particulars" 
              helperText="Item description for PDF tax invoice generation"
              variant="standard" 
              fullWidth 
              value={currentData.particulars} 
              onChange={(e) => updateCurrentData({ particulars: e.target.value })} 
              disabled={!canEdit} 
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField 
              label="Tax Indicator (ex. India SAC Code, London VAT)" 
              helperText="Tax classification code for invoice generation (e.g. India SAC: 998314, London/UK: 998314)" 
              variant="standard" 
              fullWidth 
              value={currentData.sacCode} 
              onChange={(e) => updateCurrentData({ sacCode: e.target.value })} 
              disabled={!canEdit} 
            />
          </Grid>

          {currentData.paymentMode === "paid" && (
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" fontWeight={600}>Location Fees</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={handleAddLocationFee}
                    disabled={!canEdit}
                    sx={{ color: '#0d3559', borderColor: '#0d3559', '&:hover': { borderColor: '#0b2a47', bgcolor: 'rgba(13, 53, 89, 0.04)' } }}
                  >
                    Add
                  </Button>
                </Stack>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Country</TableCell><TableCell>Fee ({currencySymbol})</TableCell><TableCell align="right">Action</TableCell></TableRow></TableHead>
                    <TableBody>
                      {currentData.locationFees.map((fee, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {fee.isDefault ? "Other Countries" : (
                              <Select variant="standard" value={fee.countryCodeId || ""} onChange={(e) => handleLocationFeeChange(idx, 'countryCodeId', e.target.value)} disabled={!canEdit} fullWidth>
                                {availableCountries.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                              </Select>
                            )}
                          </TableCell>
                          <TableCell>
                            <TextField 
                              variant="standard" 
                              type="number" 
                              inputProps={{ min: "0.01", step: "0.01" }}
                              onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault(); }}
                              value={fee.consultationFee} 
                              onChange={(e) => handleLocationFeeChange(idx, 'consultationFee', e.target.value)} 
                              disabled={!canEdit} 
                              error={fee.consultationFee !== '' && (Number.isNaN(Number(fee.consultationFee)) || Number(fee.consultationFee) <= 0 || (String(fee.consultationFee).includes('.') && String(fee.consultationFee).split('.')[1]?.length > 2))}
                              helperText={fee.consultationFee !== '' && (Number.isNaN(Number(fee.consultationFee)) || Number(fee.consultationFee) <= 0) ? "Must be > 0" : (String(fee.consultationFee).includes('.') && String(fee.consultationFee).split('.')[1]?.length > 2) ? "Max 2 decimal places" : ""}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {!fee.isDefault && <IconButton size="small" onClick={() => handleRemoveLocationFee(fee.countryCodeId)} disabled={!canEdit}><Delete color="error" /></IconButton>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 1.5, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600} color="#0d3559">
                  Show in Calendar App (Active Status)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Untick/Disable this control field to hide this {selectedConsultationTypeTab > 0 ? "consultation type" : "default consultation"} from client bookings in the calendar app.
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentData.isActive !== false}
                    onChange={(e) => updateCurrentData({ isActive: e.target.checked })}
                    disabled={!canEdit}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600} color={currentData.isActive !== false ? "#2e7d32" : "#d32f2f"}>
                    {currentData.isActive !== false ? "Active (Visible)" : "Disabled (Hidden)"}
                  </Typography>
                }
              />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentData.isSameDayBookingAllowed}
                  onChange={(e) => updateCurrentData({ isSameDayBookingAllowed: e.target.checked })}
                  disabled={!canEdit}
                  sx={{
                    color: '#ef6c00',
                    '&.Mui-checked': { color: '#ef6c00' }
                  }}
                />
              }
              label="Allow Same Day Booking"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentData.notifyByEmail}
                  onChange={(e) => updateCurrentData({ notifyByEmail: e.target.checked })}
                  disabled={!canEdit}
                  sx={{
                    color: '#ef6c00',
                    '&.Mui-checked': { color: '#ef6c00' }
                  }}
                />
              }
              label="Notify by Email"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentData.notifyBySMS}
                  onChange={(e) => updateCurrentData({ notifyBySMS: e.target.checked })}
                  disabled={!canEdit}
                  sx={{
                    color: '#ef6c00',
                    '&.Mui-checked': { color: '#ef6c00' }
                  }}
                />
              }
              label="Notify by SMS"
            />
          </Grid>

          <Grid item xs={12}>
            <Accordion variant="outlined">
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Payment Gateway Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={currentData.paymentConfiguration.isEnabled}
                      onChange={(e) => handlePaymentConfigChange('isEnabled', e.target.checked)}
                      disabled={!canEdit}
                      sx={{
                        color: '#ef6c00',
                        '&.Mui-checked': { color: '#ef6c00' }
                      }}
                    />
                  }
                  label="Razorpay enabled"
                />
                {currentData.paymentConfiguration.isEnabled && (
                  <Box sx={{ mt: 2 }}>
                    <Stack direction="row" spacing={3} mb={2}>
                      {currentData.paymentConfiguration.keys.map(k => (
                        <FormControlLabel key={k.type} control={<input type="radio" checked={k.isActive} onChange={() => handleSetActiveKey(k.type)} disabled={!canEdit} />} label={k.type ? k.type.toUpperCase() : "N/A"} />
                      ))}
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField 
                          label="Key ID" 
                          fullWidth 
                          variant="outlined" 
                          size="small" 
                          value={getActiveKey()?.keyId || ''} 
                          onChange={(e) => handleKeyUpdate(getActiveKey()?.type, 'keyId', e.target.value)} 
                          disabled={!canEdit} 
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField 
                          label="Key Secret" 
                          fullWidth 
                          variant="outlined" 
                          size="small" 
                          type={showSecrets[`${getActiveKey()?.type}KeySecret`] || showSecrets.testKeySecret ? "text" : "password"} 
                          value={getActiveKey()?.keySecret || ''} 
                          onChange={(e) => handleKeyUpdate(getActiveKey()?.type, 'keySecret', e.target.value)} 
                          disabled={!canEdit}
                          inputProps={{
                            autoComplete: "new-password",
                            name: "razorpay_key_secret_no_autofill"
                          }}
                          InputProps={{ 
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton 
                                  onClick={() => {
                                    const secretKey = `${getActiveKey()?.type}KeySecret`;
                                    setShowSecrets(prev => ({ ...prev, [secretKey]: !prev[secretKey], testKeySecret: !prev.testKeySecret }));
                                  }} 
                                  edge="end"
                                >
                                  {(showSecrets[`${getActiveKey()?.type}KeySecret`] || showSecrets.testKeySecret) ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ) 
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 2.5, mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600} color="#0d3559">
                        Webhook Configuration
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12}>
                        <TextField 
                          label="Webhook URL" 
                          fullWidth 
                          variant="outlined" 
                          size="small" 
                          value={getWebhookUrl()} 
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title="Copy Webhook URL to Clipboard">
                                  <IconButton 
                                    onClick={() => {
                                      navigator.clipboard.writeText(getWebhookUrl());
                                      dispatch(messageShow("Webhook URL copied to clipboard!"));
                                    }} 
                                    edge="end"
                                    color="primary"
                                  >
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={12}>
                        <TextField 
                          label="Webhook Secret" 
                          fullWidth 
                          variant="outlined" 
                          size="small" 
                          type={showSecrets[`${getActiveKey()?.type}WebhookSecret`] ? "text" : "password"} 
                          value={getActiveKey()?.webhookSecret || ''} 
                          onChange={(e) => handleKeyUpdate(getActiveKey()?.type, 'webhookSecret', e.target.value)} 
                          disabled={!canEdit}
                          placeholder="Enter Razorpay Webhook Secret for this environment"
                          inputProps={{
                            autoComplete: "new-password",
                            name: "razorpay_webhook_secret_no_autofill"
                          }}
                          InputProps={{ 
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton 
                                  onClick={() => {
                                    const keyName = `${getActiveKey()?.type}WebhookSecret`;
                                    setShowSecrets(prev => ({ ...prev, [keyName]: !prev[keyName] }));
                                  }} 
                                  edge="end"
                                >
                                  {showSecrets[`${getActiveKey()?.type}WebhookSecret`] ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ) 
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={12}>
                        <Alert severity="info" icon={<HelpOutline fontSize="small" />} sx={{ bgcolor: '#eef7ff', color: '#0d3559', borderRadius: 1.5, border: '1px solid #bce0fd' }}>
                          <Typography variant="body2">
                            <strong>Need configuration support?</strong> Create a user for <strong>support@datumailabs.com</strong> in your Razorpay Account with <em>Read Only / Settings</em> permissions, and our team will configure the webhooks for you.
                          </Typography>
                        </Alert>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Dialog open={consultationTypeModal.open} onClose={closeTypeModal} fullWidth maxWidth="md">
            <Typography variant="h6" sx={{ p: 2, bgcolor: '#f5f5f5' }}>{consultationTypeModal.mode === 'add' ? 'Add' : 'Edit'} Consultation Type</Typography>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {/* 1. CALENDAR AUTHORIZATION CARD AT VERY TOP */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #cbd5e1' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} color="#0d3559">
                          Calendar Authorization ({consultationTypeModal.data?.calendarProvider === 'outlook' ? 'Microsoft Outlook' : 'Google Calendar'})
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Authorize an OAuth calendar connection specifically for this Consultation Type.
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Lock />}
                        onClick={async () => {
                          try {
                            const typeId = consultationTypeModal.data?._id || '';
                            const typeIdx = consultationTypeModal.index >= 0 ? (consultationTypeModal.index + 1) : 0;
                            if (typeId) {
                              sessionStorage.setItem('oauth_consultation_type_id', typeId);
                            }
                            if (typeIdx > 0) {
                              sessionStorage.setItem('oauth_consultation_type_index', typeIdx);
                            }
                            const calendarEmail = (consultationTypeModal.data?.calendarEmail || consultationTypeModal.data?.assignedManagerEmail || consultationTypeModal.data?.email || '').trim();
                            if (calendarEmail) {
                              sessionStorage.setItem('oauth_calendar_email', calendarEmail);
                            }
                            const provider = consultationTypeModal.data?.calendarProvider || 'google';
                            const action = provider === 'outlook' ? authenticateOutlookCalendar : authenticateGoogleCalendar;
                            const res = await action({ consultationTypeId: typeId, consultationTypeIndex: typeIdx, calendarEmail });
                            if (res?.authUrl) window.location.href = res.authUrl;
                          } catch (err) {
                            dispatch(messageShow({ message: "Failed to initiate authorization", type: "error" }));
                          }
                        }}
                        sx={{ bgcolor: '#ef6c00', textTransform: 'none', '&:hover': { bgcolor: '#e65100' } }}
                      >
                        Authorize Account
                      </Button>
                    </Stack>
                    <Alert 
                      severity={consultationTypeModal.data?.calendarEmail ? "success" : "warning"} 
                      icon={consultationTypeModal.data?.calendarEmail ? <CheckCircle fontSize="inherit" /> : null}
                      sx={{ py: 0.5, mt: 1 }}
                    >
                      {consultationTypeModal.data?.calendarEmail 
                        ? `Connected Account: ${consultationTypeModal.data.calendarEmail}` 
                        : `Click Authorize Account to link a ${consultationTypeModal.data?.calendarProvider === 'outlook' ? 'Microsoft Outlook' : 'Google Calendar'} account.`
                      }
                    </Alert>
                  </Paper>
                </Grid>

                {/* 2. Control Field: Show / Hide in Calendar App */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 1.5, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} color="#0d3559">
                        Show in Calendar App (Active Status)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Untick/Disable this control field to hide this consultation type from client bookings in the calendar app.
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={consultationTypeModal.data?.isActive !== false}
                          onChange={(e) => handleTypeDataChange('isActive', e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={600} color={consultationTypeModal.data?.isActive !== false ? "#2e7d32" : "#d32f2f"}>
                          {consultationTypeModal.data?.isActive !== false ? "Active (Visible)" : "Disabled (Hidden)"}
                        </Typography>
                      }
                    />
                  </Paper>
                </Grid>

                {/* 3. Manager Assignment & Type Basics */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel>Assigned Email (Roles & Permissions)</InputLabel>
                    <Select
                      value={consultationTypeModal.data?.assignedManagerEmail || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        handleTypeDataChange('assignedManagerEmail', val);
                        const selectedUser = adminUsers.find(u => u.email === val);
                        if (selectedUser) {
                          const userName = selectedUser.name || selectedUser.firstName || '';
                          handleTypeDataChange('assignedManagerName', userName);
                          handleTypeDataChange('assignedManagerId', selectedUser._id || '');
                          handleTypeDataChange('assignedManagerRole', selectedUser.roleDisplayName || '');
                          if (!consultationTypeModal.data?.calendarEmail) {
                            handleTypeDataChange('calendarEmail', val);
                          }
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>Unassigned / Default Admin</em>
                      </MenuItem>
                      {consultationTypeModal.data?.assignedManagerEmail && !adminUsers.some(u => u.email === consultationTypeModal.data.assignedManagerEmail) && (
                        <MenuItem value={consultationTypeModal.data.assignedManagerEmail}>
                          {consultationTypeModal.data.assignedManagerEmail}
                        </MenuItem>
                      )}
                      {adminUsers.map((user) => {
                        const roleName = user.roleDisplayName || 'Store Admin';
                        const displayName = `${roleName} — ${user.email}`;
                        return (
                          <MenuItem key={user._id || user.email} value={user.email}>
                            {displayName}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField label="Type Name" fullWidth variant="standard" value={consultationTypeModal.data?.name || ''} onChange={(e) => handleTypeDataChange('name', e.target.value)} placeholder="e.g. AI Strategy Session" />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel>Payment Mode</InputLabel>
                    <Select value={consultationTypeModal.data?.paymentMode || 'unpaid'} onChange={(e) => handleTypeDataChange('paymentMode', e.target.value)}>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="unpaid">Free (Unpaid)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel>Duration</InputLabel>
                    <Select value={consultationTypeModal.data?.meetingDurationMinutes || 15} onChange={(e) => {
                      const val = Number(e.target.value);
                      handleTypeDataChange('meetingDurationMinutes', val);
                      handleTypeDataChange('eventDurationMinutes', val);
                    }}>
                      {[15, 30, 45, 60, 90, 120].map(v => <MenuItem key={v} value={v}>{v} min</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField label="Description" fullWidth variant="standard" multiline rows={2} value={consultationTypeModal.data?.description || ''} onChange={(e) => handleTypeDataChange('description', e.target.value)} />
                </Grid>

                {/* 3. Provider & Calendar Email & Embed URL */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel>Calendar Provider</InputLabel>
                    <Select value={consultationTypeModal.data?.calendarProvider || 'google'} onChange={(e) => handleTypeDataChange('calendarProvider', e.target.value)}>
                      <MenuItem value="google">Google Calendar</MenuItem>
                      <MenuItem value="outlook">Outlook Calendar</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField 
                    label="Calendar Email / Account" 
                    fullWidth 
                    variant="standard" 
                    value={consultationTypeModal.data?.calendarEmail || ''} 
                    onChange={(e) => handleTypeDataChange('calendarEmail', e.target.value)} 
                    placeholder="e.g. calendar@domain.com" 
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField 
                    label="Calendar Embed URL" 
                    fullWidth 
                    variant="standard" 
                    value={consultationTypeModal.data?.calendarUrl || ''} 
                    onChange={(e) => handleTypeDataChange('calendarUrl', e.target.value)} 
                    placeholder="https://calendar.google.com/calendar/embed?src=..." 
                  />
                </Grid>

                {/* 4. Form & Lead Config */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel>Default Lead Status</InputLabel>
                    <Select value={consultationTypeModal.data?.leadStatus || 'Contact'} onChange={(e) => handleTypeDataChange('leadStatus', e.target.value)}>
                      <MenuItem value="Contact">Contact</MenuItem>
                      <MenuItem value="Lead">Lead</MenuItem>
                      <MenuItem value="Prospect">Prospect</MenuItem>
                      <MenuItem value="Customer">Customer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel>Form Type</InputLabel>
                    <Select value={consultationTypeModal.data?.formType || 'full'} onChange={(e) => handleTypeDataChange('formType', e.target.value)}>
                      <MenuItem value="full">Full Form</MenuItem>
                      <MenuItem value="mini">Mini Form</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField 
                    label="Particulars" 
                    helperText="Item description for PDF tax invoice generation"
                    fullWidth 
                    variant="standard" 
                    value={consultationTypeModal.data?.particulars || 'Consultation Services'} 
                    onChange={(e) => handleTypeDataChange('particulars', e.target.value)} 
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField 
                    label="Tax Indicator (ex. India SAC Code, London VAT)" 
                    helperText="Tax classification code (e.g. India SAC: 998314, London/UK: 998314)" 
                    fullWidth 
                    variant="standard" 
                    value={consultationTypeModal.data?.sacCode || '998314'} 
                    onChange={(e) => handleTypeDataChange('sacCode', e.target.value)} 
                  />
                </Grid>

                {/* 5. Checkboxes */}
                <Grid item xs={12}>
                  <Stack direction="row" spacing={3} flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={consultationTypeModal.data?.isSameDayBookingAllowed ?? true} 
                          onChange={(e) => handleTypeDataChange('isSameDayBookingAllowed', e.target.checked)} 
                        />
                      }
                      label="Allow Same Day Booking"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={!!consultationTypeModal.data?.notifyByEmail} 
                          onChange={(e) => handleTypeDataChange('notifyByEmail', e.target.checked)} 
                        />
                      }
                      label="Notify by Email"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={!!consultationTypeModal.data?.notifyBySMS} 
                          onChange={(e) => handleTypeDataChange('notifyBySMS', e.target.checked)} 
                        />
                      }
                      label="Notify by SMS"
                    />
                  </Stack>
                </Grid>
                
                {consultationTypeModal.data?.paymentMode === 'paid' && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" fontWeight={600}>Location Fees (Type Specific)</Typography>
                        <Button size="small" startIcon={<Add />} onClick={handleAddTypeLocationFee}>Add Fee</Button>
                      </Stack>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead><TableRow><TableCell>Country</TableCell><TableCell>Fee</TableCell><TableCell align="right">Action</TableCell></TableRow></TableHead>
                          <TableBody>
                            {consultationTypeModal.data.locationFees.map((fee, fIdx) => (
                              <TableRow key={fIdx}>
                                <TableCell>
                                  {fee.isDefault ? "Other Countries" : (
                                    <Select variant="standard" value={fee.countryCodeId || ""} onChange={(e) => handleTypeLocationFeeChange(fIdx, 'countryCodeId', e.target.value)} fullWidth>
                                      {availableCountries.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                                    </Select>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <TextField 
                                    variant="standard" 
                                    type="number" 
                                    inputProps={{ min: "0.01", step: "0.01" }}
                                    onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault(); }}
                                    value={fee.consultationFee} 
                                    onChange={(e) => handleTypeLocationFeeChange(fIdx, 'consultationFee', e.target.value)} 
                                    error={fee.consultationFee !== '' && (Number.isNaN(Number(fee.consultationFee)) || Number(fee.consultationFee) <= 0 || (String(fee.consultationFee).includes('.') && String(fee.consultationFee).split('.')[1]?.length > 2))}
                                    helperText={fee.consultationFee !== '' && (Number.isNaN(Number(fee.consultationFee)) || Number(fee.consultationFee) <= 0) ? "Must be > 0" : (String(fee.consultationFee).includes('.') && String(fee.consultationFee).split('.')[1]?.length > 2) ? "Max 2 decimal places" : ""}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {!fee.isDefault && <IconButton size="small" color="error" onClick={() => {
                                    const fees = consultationTypeModal.data.locationFees.filter((_, i) => i !== fIdx);
                                    handleTypeDataChange('locationFees', fees);
                                  }}><Delete /></IconButton>}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>Meeting Hours (Optional Override)</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={{ border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                      {consultationTypeModal.data?.workingHours.map((day, dIdx) => (
                        <Grid container spacing={1} key={day.day} alignItems="center" mb={0.5}>
                          <Grid item xs={3}><Typography variant="body2">{day.day}</Typography></Grid>
                          <Grid item xs={2}><Switch size="small" checked={day.isClosed} onChange={() => toggleTypeDayClosed(dIdx)} /></Grid>
                          <Grid item xs={7}>
                            {!day.isClosed && (
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <TimePicker value={dayjs(day.startTime, 'HH:mm')} onChange={(v) => handleTypeTimeChange(dIdx, 'startTime', v)} slotProps={{ textField: { size: 'small', variant: 'standard' } }} />
                                <Typography variant="caption">to</Typography>
                                <TimePicker value={dayjs(day.endTime, 'HH:mm')} onChange={(v) => handleTypeTimeChange(dIdx, 'endTime', v)} slotProps={{ textField: { size: 'small', variant: 'standard' } }} />
                              </Stack>
                            )}
                          </Grid>
                        </Grid>
                      ))}
                    </Box>
                  </LocalizationProvider>
                </Grid>
              </Grid>
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                <Button onClick={closeTypeModal}>Cancel</Button>
                <Button variant="contained" onClick={handleSaveType} sx={{ bgcolor: '#0d3559' }}>Save Type</Button>
              </Stack>
            </DialogContent>
          </Dialog>

          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>Meeting Hours</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2 }}>
                {currentData.workingHours.map((day, idx) => (
                  <Grid container spacing={2} key={day.day} alignItems="center" mb={1}>
                    <Grid item xs={4}><Typography>{day.day}</Typography></Grid>
                    <Grid item xs={3}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={day.isClosed}
                            onChange={() => toggleDayClosed(idx)}
                            disabled={!canEdit}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#ef6c00' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ef6c00' }
                            }}
                          />
                        }
                        label="Closed"
                      />
                    </Grid>
                    <Grid item xs={5}>
                      {!day.isClosed && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TimePicker label="Start" value={dayjs(day.startTime, 'HH:mm')} onChange={(v) => handleTimeChange(idx, 'startTime', v)} disabled={!canEdit} slotProps={{ textField: { size: 'small', variant: 'standard' } }} />
                          <Typography>to</Typography>
                          <TimePicker label="End" value={dayjs(day.endTime, 'HH:mm')} onChange={(v) => handleTimeChange(idx, 'endTime', v)} disabled={!canEdit} slotProps={{ textField: { size: 'small', variant: 'standard' } }} />
                        </Stack>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Box>
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={2} sx={{ mt: 3, flexWrap: 'wrap', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || !canEdit}
                sx={{ bgcolor: '#0d3559', color: 'white', '&:hover': { bgcolor: '#0b2a47' }, textTransform: 'none', px: 3 }}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                disabled={isSaving || !canEdit}
                sx={{ color: '#0d3559', borderColor: '#0d3559', '&:hover': { borderColor: '#0b2a47', bgcolor: 'rgba(13, 53, 89, 0.04)' }, textTransform: 'none' }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={<Stop />}
                onClick={handleStopMeeting}
                disabled={!canEdit}
                sx={{ bgcolor: '#ef6c00', color: 'white', '&:hover': { bgcolor: '#e65100' }, textTransform: 'none' }}
              >
                Stop Bookings
              </Button>
              {(activeTab === "google" || activeTab === "outlook") && (
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdf />}
                  onClick={() => setManualOpen(true)}
                  sx={{ bgcolor: '#0d3559', color: 'white', '&:hover': { bgcolor: '#0b2a47' }, textTransform: 'none' }}
                >
                  View Manual
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
});

export default Setup;